import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Link, useNavigate } from 'react-router-dom';
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Tabs,
  Typography,
  Alert,
  Spin,
  App,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  ExportOutlined,
  HistoryOutlined,
  PrinterOutlined,
  EditOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useMaterialRequest,
  useSubmitMaterialRequest,
  useApproveMaterialRequest,
  useRejectMaterialRequest,
  useCancelMaterialRequest,
} from '../../hooks/useMaterialRequest';
import { useQueryClient } from '@tanstack/react-query';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { RejectionReasonModal } from '@/components/rejection/RejectionReasonModal';
import { ENTITY_TYPE_MATERIAL_REQUEST } from '@/constants/approval-entity-types';
import type { RejectionPayload } from '@/services/rejection.service';
import materialRequestService from '@/services/materialRequest.service';
import { useUserDisplayResolver } from '../../hooks/useUserDisplayResolver';
import { MaterialRequest, type MaterialRequestItem } from '../../services/materialRequest.service';
import { documentLinePieces, documentLineSize } from '@/utils/inventory/measurementQuantity';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import {
  getCriticalItemRowClassName,
  isItemCritical,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { MtcDocumentLink } from '@/components/documents/MtcDocumentLink';
import { useCurrency } from '@/contexts/CurrencyContext';
import materialIssuanceService, { MaterialIssuance } from '@/services/materialIssuance.service';
import { PrintableMaterialIssueVoucher } from './PrintableMaterialIssueVoucher';
import { PrintableVoucher } from '@/components/sales-invoice/PrintableVoucher';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import { canAccessRouteByPermissions } from '@/config/routePermissions';

const { Title, Text } = Typography;

interface MaterialRequestDetailModalProps {
  visible: boolean;
  materialRequestId: string | null;
  onClose: () => void;
  onIssue?: (requestId: string) => void;
  renderAsPage?: boolean;
}

export const MaterialRequestDetailModal: React.FC<MaterialRequestDetailModalProps> = ({
  visible,
  materialRequestId,
  onClose,
  onIssue,
  renderAsPage = false,
}) => {
  const { modal, message } = App.useApp();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const { resolve } = useUserDisplayResolver();
  const user = useAuthStore((s) => s.user);
  const isSuper = isPlatformSuperAdmin(user);
  const perms = user?.permissions ?? [];
  const canIssueMaterials = isSuper || perms.includes('inventory.material_issue.create');
  const canEdit =
    isSuper ||
    perms.includes('inventory.material_request.edit') ||
    canAccessRouteByPermissions('/scm/inventory/material-requests/:id/edit', perms, isSuper);
  const canCancel =
    isSuper ||
    perms.includes('inventory.material_request.cancel') ||
    perms.includes('inventory.material_request.create');
  const [activeTab, setActiveTab] = useState('items');
  const [toolbarRejectOpen, setToolbarRejectOpen] = useState(false);
  const [printingIssuanceId, setPrintingIssuanceId] = useState<string | null>(null);
  const [printingAccountingVoucherForIssuanceId, setPrintingAccountingVoucherForIssuanceId] =
    useState<string | null>(null);

  const { data: materialRequest, isLoading } = useMaterialRequest(materialRequestId || '', {
    enabled: (visible || renderAsPage) && !!materialRequestId,
  });
  const { data: departments = [] } = useDepartmentsForSelection();

  const submitForApproval = useSubmitMaterialRequest();
  const approveMutation = useApproveMaterialRequest();
  const rejectMutation = useRejectMaterialRequest();
  const cancelMutation = useCancelMaterialRequest();

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'Draft': 'default',
      'Pending': 'warning',
      'Approved': 'success',
      'Rejected': 'error',
      'Partially Issued': 'processing',
      'Fully Issued': 'success',
      'Closed': 'default',
      'Cancelled': 'error',
    };
    return colorMap[status] || 'default';
  };

  const getApprovalStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'Not Required': 'default',
      'Pending': 'warning',
      'Approved': 'success',
      'Rejected': 'error',
    };
    return colorMap[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colorMap: Record<string, string> = {
      'Low': 'blue',
      'Normal': 'default',
      'High': 'orange',
      'Urgent': 'red',
    };
    return colorMap[priority] || 'default';
  };

  const handleSubmitForApproval = async () => {
    if (!materialRequest) return;

    modal.confirm({
      title: 'Submit Material Request',
      content:
        Number(materialRequest.total_estimated_cost || 0) > 0
          ? 'Submit this material request? It may go for approval based on the company threshold.'
          : 'Submit this material request? With zero estimated cost it will be approved immediately (no approval workflow).',
      onOk: async () => {
        await submitForApproval.mutateAsync(materialRequest.id);
      },
    });
  };

  const handleApprove = async () => {
    if (!materialRequest) return;
    
    modal.confirm({
      title: 'Approve Material Request',
      content: 'Are you sure you want to approve this material request?',
      onOk: async () => {
        try {
          await approveMutation.mutateAsync(materialRequest.id);
          message.success('Material request approved successfully');
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to approve material request');
        }
      },
    });
  };

  const handleReject = () => {
    if (!materialRequest) return;
    setToolbarRejectOpen(true);
  };

  const handleCancelRequest = () => {
    if (!materialRequest) return;
    modal.confirm({
      title: 'Cancel Material Request',
      content: `Cancel ${materialRequest.request_number}? This cannot be undone.`,
      okText: 'Cancel Request',
      okButtonProps: { danger: true },
      onOk: async () => {
        await cancelMutation.mutateAsync(materialRequest.id);
      },
    });
  };

  const handleToolbarRejectConfirm = async (payload: RejectionPayload) => {
    if (!materialRequest) return;
    try {
      await rejectMutation.mutateAsync({ id: materialRequest.id, payload });
      message.success('Material request rejected');
      setToolbarRejectOpen(false);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to reject material request');
    }
  };

  const handleIssueMaterials = () => {
    if (materialRequest && onIssue) {
      onIssue(materialRequest.id);
    }
  };

  const handlePrintIssuanceVoucher = React.useCallback(
    async (issuanceId: string) => {
      try {
        setPrintingIssuanceId(issuanceId);
        const issuance = await materialIssuanceService.getById(issuanceId);
        const mainContent = document.getElementById('root');
        if (mainContent) mainContent.style.display = 'none';
        const printContainer = document.createElement('div');
        printContainer.id = 'print-container';
        printContainer.style.display = 'block';
        document.body.appendChild(printContainer);
        const root = ReactDOM.createRoot(printContainer);
        const cleanup = () => {
          root.unmount();
          if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
          if (mainContent) mainContent.style.display = 'block';
          window.removeEventListener('afterprint', cleanup);
        };
        root.render(
          <PrintableMaterialIssueVoucher
            issuance={issuance as MaterialIssuance}
            onClose={cleanup}
          />
        );
        window.addEventListener('afterprint', cleanup);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to load material issue voucher');
      } finally {
        setPrintingIssuanceId(null);
      }
    },
    [message]
  );

  const handlePrintAccountingVoucher = React.useCallback(
    async (issuanceId: string) => {
      try {
        setPrintingAccountingVoucherForIssuanceId(issuanceId);
        const issuance = await materialIssuanceService.getById(issuanceId);
        const journalVoucherId = issuance.journal_voucher_id;
        if (!journalVoucherId) {
          message.warning('No accounting voucher linked. Post/issue first to generate JV.');
          return;
        }
        const voucherRes = await api.get(`/finance/vouchers/${journalVoucherId}`);
        const voucher = (voucherRes as any)?.data?.data ?? (voucherRes as any)?.data ?? voucherRes;
        if (!voucher?.id) {
          message.warning('Accounting voucher not found for this issuance.');
          return;
        }
        const mainContent = document.getElementById('root');
        if (mainContent) mainContent.style.display = 'none';
        const printContainer = document.createElement('div');
        printContainer.id = 'print-container';
        printContainer.style.display = 'block';
        document.body.appendChild(printContainer);
        const root = ReactDOM.createRoot(printContainer);
        const cleanup = () => {
          root.unmount();
          if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
          if (mainContent) mainContent.style.display = 'block';
          window.removeEventListener('afterprint', cleanup);
        };
        root.render(
          <PrintableVoucher
            voucher={voucher}
            referenceLabel="Material Issue"
            onClose={cleanup}
          />
        );
        window.addEventListener('afterprint', cleanup);
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Failed to load accounting voucher');
      } finally {
        setPrintingAccountingVoucherForIssuanceId(null);
      }
    },
    [message]
  );

  const issuanceColumns = useMemo(
    () => [
      {
        title: 'Issuance #',
        dataIndex: 'issuance_number',
        key: 'issuance_number',
        width: 140,
        render: (num: string, record: { id: string }) => (
          <Link to={`/scm/inventory/material-issues/${record.id}`}>{num}</Link>
        ),
      },
      {
        title: 'Date',
        dataIndex: 'issuance_date',
        key: 'issuance_date',
        width: 170,
        render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY HH:mm') : '-'),
      },
      {
        title: 'Type',
        dataIndex: 'issuance_type',
        key: 'issuance_type',
        width: 100,
        render: (t: string) => <Tag color={t === 'Full' ? 'green' : 'blue'}>{t}</Tag>,
      },
      {
        title: 'Warehouse',
        dataIndex: 'source_warehouse_id',
        key: 'source_warehouse_id',
        width: 120,
        ellipsis: true,
      },
      {
        title: 'Total cost',
        dataIndex: 'total_cost',
        key: 'total_cost',
        width: 110,
        align: 'right' as const,
        render: (v: number | string) => {
          const n = typeof v === 'string' ? parseFloat(v) : Number(v || 0);
          return formatCurrency(Number.isFinite(n) ? n : 0);
        },
      },
      {
        title: 'Issued by',
        dataIndex: 'issued_by',
        key: 'issued_by',
        width: 160,
        ellipsis: true,
        render: (uid: string) => resolve(uid, '—'),
      },
      {
        title: 'Action',
        key: 'action',
        width: 260,
        fixed: 'right' as const,
        render: (_: unknown, record: { id: string }) => (
          <Space size="small">
            <Button
              size="small"
              icon={<PrinterOutlined />}
              loading={printingIssuanceId === (record as any).id}
              onClick={() => handlePrintIssuanceVoucher((record as any).id)}
            >
              Print
            </Button>
            <Button
              size="small"
              icon={<PrinterOutlined />}
              loading={printingAccountingVoucherForIssuanceId === (record as any).id}
              onClick={() => handlePrintAccountingVoucher((record as any).id)}
            >
              Print Voucher
            </Button>
          </Space>
        ),
      },
    ],
    [
      resolve,
      formatCurrency,
      printingIssuanceId,
      handlePrintIssuanceVoucher,
      printingAccountingVoucherForIssuanceId,
      handlePrintAccountingVoucher,
    ]
  );

  const itemColumns = [
    {
      title: '#',
      dataIndex: 'line_number',
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Item Code',
      dataIndex: 'item_code',
      width: 140,
      render: (code: string, record: { is_critical?: boolean }) => (
        <ItemCodeWithCritical code={code} critical={isItemCritical(record)} />
      ),
    },
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      width: 200,
    },
    {
      title: 'BOQ line',
      key: 'boq_line',
      width: 200,
      ellipsis: true,
      render: (_: unknown, record: { work_order_line_item_id?: string | null; boq_line_label?: string | null }) =>
        record.boq_line_label || (record.work_order_line_item_id ? 'Linked' : '—'),
    },
    {
      title: 'Heat No.',
      dataIndex: 'heat_number',
      width: 110,
      render: (v: string | undefined, record: { is_critical?: boolean; heat_number_mandatory?: boolean }) =>
        record.is_critical && record.heat_number_mandatory ? (v?.trim() ? v : '—') : '—',
    },
    {
      title: 'Dimension',
      dataIndex: 'dimension',
      width: 110,
      render: (v: string | undefined, record: { is_critical?: boolean; heat_number_mandatory?: boolean }) =>
        record.is_critical && record.heat_number_mandatory ? (v?.trim() ? v : '—') : '—',
    },
    {
      title: 'Size (L×W×H)',
      key: 'measured_dimensions',
      width: 140,
      render: (_: unknown, record: MaterialRequestItem) => documentLineSize(record) || '—',
    },
    {
      title: 'Pieces',
      key: 'pieces',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, record: MaterialRequestItem) => documentLinePieces(record) ?? '—',
    },
    {
      title: 'Requested Qty',
      dataIndex: 'requested_quantity',
      width: 120,
      render: (qty: number | string, record: any) => {
        const numQty = typeof qty === 'string' ? parseFloat(qty) : qty;
        return `${numQty?.toFixed(2) || '0.00'} ${record.uom}`;
      },
    },
    {
      title: 'Issued Qty',
      dataIndex: 'issued_quantity',
      width: 120,
      render: (qty: number | string, record: any) => {
        const numQty = typeof qty === 'string' ? parseFloat(qty) : qty;
        return `${numQty?.toFixed(2) || '0.00'} ${record.uom}`;
      },
    },
    {
      title: 'Pending Qty',
      dataIndex: 'pending_quantity',
      width: 120,
      render: (qty: number | string, record: any) => {
        const numQty = typeof qty === 'string' ? parseFloat(qty) : qty;
        const pendingQty = numQty?.toFixed(2) || '0.00';
        return (
          <Text type={parseFloat(pendingQty) > 0 ? 'warning' : 'secondary'}>
            {pendingQty} {record.uom}
          </Text>
        );
      },
    },
    {
      title: 'Reserved',
      dataIndex: 'reserved_quantity',
      width: 110,
      render: (qty: number | string | undefined, record: any) => {
        const numQty = typeof qty === 'string' ? parseFloat(qty) : Number(qty ?? 0);
        const n = Number.isFinite(numQty) ? numQty : 0;
        return (
          <Text type={n > 0 ? 'warning' : 'secondary'}>
            {n.toFixed(2)} {record.uom}
          </Text>
        );
      },
    },
    {
      title: 'Est. Unit Cost',
      dataIndex: 'estimated_unit_cost',
      width: 120,
      render: (cost: number | string) => {
        const numCost = typeof cost === 'string' ? parseFloat(cost) : cost;
        const n = numCost != null && Number.isFinite(Number(numCost)) ? Number(numCost) : 0;
        return formatCurrency(n);
      },
    },
    {
      title: 'Est. Total',
      dataIndex: 'estimated_total_cost',
      width: 120,
      render: (cost: number | string) => {
        const numCost = typeof cost === 'string' ? parseFloat(cost) : cost;
        const n = numCost != null && Number.isFinite(Number(numCost)) ? Number(numCost) : 0;
        return formatCurrency(n);
      },
    },
    {
      title: 'BOM Ref',
      dataIndex: 'bom_reference',
      width: 80,
      render: (isRef: boolean) => isRef ? <Tag color="blue">BOM</Tag> : <Tag>Manual</Tag>,
    },
  ];

  const renderActions = () => {
    if (!materialRequest) return null;

    const actions: React.ReactNode[] = [];

    // Draft status - edit, submit, cancel
    if (materialRequest.request_status === 'Draft') {
      if (canEdit) {
        actions.push(
          <Button
            key="edit"
            icon={<EditOutlined />}
            onClick={() => navigate(`/scm/inventory/material-requests/${materialRequest.id}/edit`)}
          >
            Edit
          </Button>
        );
      }
      actions.push(
        <Button
          key="submit"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSubmitForApproval}
          loading={submitForApproval.isPending}
        >
          {Number(materialRequest.total_estimated_cost || 0) > 0
            ? 'Submit for Approval'
            : 'Submit'}
        </Button>
      );
      if (canCancel) {
        actions.push(
          <Button
            key="cancel"
            danger
            icon={<StopOutlined />}
            onClick={handleCancelRequest}
            loading={cancelMutation.isPending}
          >
            Cancel Request
          </Button>
        );
      }
    } else if (
      canCancel &&
      !['Fully Issued', 'Closed', 'Cancelled'].includes(materialRequest.request_status)
    ) {
      actions.push(
        <Button
          key="cancel"
          danger
          icon={<StopOutlined />}
          onClick={handleCancelRequest}
          loading={cancelMutation.isPending}
        >
          Cancel Request
        </Button>
      );
    }

    // Pending approval - can approve or reject
    if (materialRequest.approval_status === 'Pending' && !materialRequest.approval_instance_id) {
      actions.push(
        <Button
          key="approve"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleApprove}
          loading={approveMutation.isPending}
        >
          Approve
        </Button>,
        <Button
          key="reject"
          danger
          icon={<CloseCircleOutlined />}
          onClick={handleReject}
          loading={rejectMutation.isPending}
        >
          Reject
        </Button>
      );
    }

    // Approved — issue only when caller provides onIssue and user can create issuances
    if (
      onIssue &&
      canIssueMaterials &&
      (materialRequest.request_status === 'Approved' ||
        materialRequest.request_status === 'Partially Issued')
    ) {
      actions.push(
        <Button
          key="issue"
          type="primary"
          icon={<ExportOutlined />}
          onClick={handleIssueMaterials}
        >
          Issue Materials
        </Button>
      );
    }

    return actions.length > 0 ? <Space>{actions}</Space> : null;
  };

  const content = isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : materialRequest ? (
        <>
          {materialRequest.approval_instance_id ? (
            <div style={{ marginBottom: 16 }}>
              <ApprovalPanel
                entityType={ENTITY_TYPE_MATERIAL_REQUEST}
                entityId={materialRequest.id}
                onApproveViaDocument={async (remarks) => {
                  await materialRequestService.approve(materialRequest.id, remarks);
                  queryClient.invalidateQueries({ queryKey: ['material-requests'] });
                }}
                onRejectViaDocument={async (payload) => {
                  await materialRequestService.reject(materialRequest.id, payload);
                  queryClient.invalidateQueries({ queryKey: ['material-requests'] });
                }}
                extraInvalidateQueryKeys={[['material-requests'], ['material-requests', materialRequest.id]]}
              />
            </div>
          ) : null}
          {/* Header Information */}
          <Descriptions bordered column={3} size="small" style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Request Number" span={1}>
              <Text strong>{materialRequest.request_number}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Work Order" span={1}>
              {materialRequest.work_order_number ? (
                <Link to={`/mfg/work-orders/${materialRequest.work_order_id}`}>
                  {materialRequest.work_order_number}
                </Link>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Project" span={1}>
              {materialRequest.project_summary ? (
                <Text>
                  {materialRequest.project_summary.project_number}{' '}
                  {materialRequest.project_summary.project_name}
                </Text>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Context" span={1}>
              <Text>{materialRequest.context_label || materialRequest.context_kind || '—'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Request type" span={1}>
              <Tag color={materialRequest.request_type === 'BOM' ? 'blue' : 'green'}>
                {materialRequest.request_type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Priority" span={1}>
              <Tag color={getPriorityColor(materialRequest.priority)}>
                {materialRequest.priority}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Request Status" span={1}>
              <Tag color={getStatusColor(materialRequest.request_status)}>
                {materialRequest.request_status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Approval Status" span={1}>
              <Tag color={getApprovalStatusColor(materialRequest.approval_status)}>
                {materialRequest.approval_status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total Est. Cost" span={1}>
              <Text strong>
                {formatCurrency(
                  typeof materialRequest.total_estimated_cost === 'string'
                    ? parseFloat(materialRequest.total_estimated_cost) || 0
                    : materialRequest.total_estimated_cost ?? 0
                )}
              </Text>
            </Descriptions.Item>

            <Descriptions.Item label="Request Date" span={1}>
              {dayjs(materialRequest.request_date).format('DD MMM YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Required Date" span={1}>
              {materialRequest.required_date ? dayjs(materialRequest.required_date).format('DD MMM YYYY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Source Warehouse" span={1}>
              {materialRequest.source_warehouse_id || '-'}
            </Descriptions.Item>

            <Descriptions.Item label="Department" span={1}>
              {materialRequest.department_id
                ? (() => {
                    const d = departments.find((x) => x.id === materialRequest.department_id);
                    return d ? `${d.department_code} - ${d.department_name}` : materialRequest.department_id;
                  })()
                : '—'}
            </Descriptions.Item>

            <Descriptions.Item label="Requested By" span={1}>
              {resolve(materialRequest.requested_by, '—')}
            </Descriptions.Item>
            <Descriptions.Item label="Approved By" span={1}>
              {materialRequest.approved_by ? resolve(materialRequest.approved_by) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Approved Date" span={1}>
              {materialRequest.approved_date ? dayjs(materialRequest.approved_date).format('DD MMM YYYY HH:mm') : '-'}
            </Descriptions.Item>
          </Descriptions>

          {materialRequest.notes && (
            <Alert
              message="Notes"
              description={materialRequest.notes}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          {/* Tabs for Items, Issuances, History */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'items',
                label: `Items (${materialRequest.items?.length || 0})`,
                children: (
                  <Table
                    dataSource={materialRequest.items || []}
                    columns={itemColumns}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 1000 }}
                    rowClassName={(r) => getCriticalItemRowClassName(r)}
                    summary={(pageData) => {
                      const totalCost = pageData.reduce((sum, item) => {
                        const cost = typeof item.estimated_total_cost === 'string' 
                          ? parseFloat(item.estimated_total_cost) 
                          : item.estimated_total_cost;
                        return sum + (cost || 0);
                      }, 0);

                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={9} align="right">
                              <Text strong>Total:</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>
                              <Text strong>{formatCurrency(totalCost)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} />
                          </Table.Summary.Row>
                        </Table.Summary>
                      );
                    }}
                  />
                ),
              },
              {
                key: 'issuances',
                label: `Issuances (${materialRequest.issuances?.length || 0})`,
                children: (() => {
                  const issuanceList = [...(materialRequest.issuances ?? [])].sort(
                    (a, b) => dayjs(b.issuance_date).valueOf() - dayjs(a.issuance_date).valueOf()
                  );
                  const mrItems = materialRequest.items ?? [];
                  const resolveMrLine = (line: { material_request_item_id?: string; item_id?: string }) =>
                    mrItems.find((i) => i.id === line.material_request_item_id) ||
                    mrItems.find((i) => i.item_id === line.item_id);
                  const lineColumns: ColumnsType<Record<string, unknown>> = [
                    {
                      title: 'Item code',
                      key: 'item_code',
                      width: 140,
                      render: (_: unknown, line: Record<string, unknown>) => {
                        const mr = resolveMrLine(
                          line as { material_request_item_id?: string; item_id?: string }
                        );
                        return (
                          <ItemCodeWithCritical
                            code={mr?.item_code}
                            critical={isItemCritical(mr)}
                          />
                        );
                      },
                    },
                    {
                      title: 'Item name',
                      key: 'item_name',
                      width: 180,
                      ellipsis: true,
                      render: (_: unknown, line: Record<string, unknown>) =>
                        resolveMrLine(line as { material_request_item_id?: string; item_id?: string })?.item_name || '—',
                    },
                    {
                      title: 'Qty issued',
                      dataIndex: 'issued_quantity',
                      key: 'issued_quantity',
                      width: 120,
                      render: (q: unknown, line: Record<string, unknown>) => {
                        const n = typeof q === 'string' ? parseFloat(q) : Number(q);
                        const uom = typeof line.uom === 'string' ? line.uom : '';
                        return `${Number.isFinite(n) ? n.toFixed(2) : '0.00'} ${uom}`;
                      },
                    },
                    {
                      title: 'Unit cost',
                      dataIndex: 'unit_cost',
                      key: 'unit_cost',
                      width: 100,
                      align: 'right' as const,
                      render: (v: number | string) => {
                        const n = typeof v === 'string' ? parseFloat(v) : Number(v || 0);
                        return formatCurrency(Number.isFinite(n) ? n : 0);
                      },
                    },
                    {
                      title: 'Line total',
                      dataIndex: 'total_cost',
                      key: 'total_cost',
                      width: 100,
                      align: 'right' as const,
                      render: (v: number | string) => {
                        const n = typeof v === 'string' ? parseFloat(v) : Number(v || 0);
                        return formatCurrency(Number.isFinite(n) ? n : 0);
                      },
                    },
                    {
                      title: 'Heat',
                      dataIndex: 'heat_number',
                      key: 'heat_number',
                      width: 100,
                      render: (v: unknown) => (v != null && String(v).trim() !== '' ? String(v) : '—'),
                    },
                    {
                      title: 'Dimension',
                      dataIndex: 'dimension',
                      key: 'dimension',
                      width: 100,
                      render: (v: unknown) => (v != null && String(v).trim() !== '' ? String(v) : '—'),
                    },
                    {
                      title: 'MTC document',
                      key: 'mtc_document',
                      width: 280,
                      render: (_: unknown, line: Record<string, unknown>) => (
                        <MtcDocumentLink
                          documentId={
                            typeof line.mtc_document_id === 'string' ? line.mtc_document_id : null
                          }
                          documentNumber={
                            typeof line.mtc_document_number === 'string'
                              ? line.mtc_document_number
                              : null
                          }
                          compact
                        />
                      ),
                    },
                  ];

                  if (issuanceList.length === 0) {
                    return (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        <HistoryOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <div>No issuances recorded for this request yet.</div>
                        <Text type="secondary">
                          Store fulfillment happens on{' '}
                          <Link to="/scm/inventory/material-issues">Material Issues</Link>.
                        </Text>
                      </div>
                    );
                  }

                  return (
                    <Table
                      dataSource={issuanceList}
                      columns={issuanceColumns}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      scroll={{ x: 900 }}
                      expandable={{
                        expandedRowRender: (record: any) => (
                          <Table
                            size="small"
                            pagination={false}
                            dataSource={record.items || []}
                            rowKey="id"
                            columns={lineColumns}
                            rowClassName={(line: Record<string, unknown>) =>
                              getCriticalItemRowClassName(
                                resolveMrLine(
                                  line as { material_request_item_id?: string; item_id?: string }
                                )
                              )
                            }
                          />
                        ),
                        rowExpandable: (record: any) => (record.items?.length ?? 0) > 0,
                      }}
                    />
                  );
                })(),
              },
            ]}
          />
        </>
      ) : (
        <Alert
          message="Material Request Not Found"
          description="The requested material request could not be found."
          type="error"
          showIcon
        />
      );

  if (renderAsPage) {
    return (
      <>
        <div style={{ padding: 24 }}>
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <Space>
              <Title level={4} style={{ margin: 0 }}>
                Material Request Details
              </Title>
              {materialRequest ? (
                <Tag color={getStatusColor(materialRequest.request_status)}>
                  {materialRequest.request_status}
                </Tag>
              ) : null}
            </Space>
            <Space>
              {renderActions()}
              <Button onClick={onClose}>Close</Button>
            </Space>
          </div>
          {content}
        </div>
        <RejectionReasonModal
          open={toolbarRejectOpen}
          documentType={ENTITY_TYPE_MATERIAL_REQUEST}
          documentLabel={materialRequest?.request_number ? `Request ${materialRequest.request_number}` : undefined}
          confirmLoading={rejectMutation.isPending}
          onCancel={() => setToolbarRejectOpen(false)}
          onConfirm={handleToolbarRejectConfirm}
        />
      </>
    );
  }

  return (
    <>
      <Modal
        title={
          <Space>
            <Text strong>Material Request Details</Text>
            {materialRequest && (
              <Tag color={getStatusColor(materialRequest.request_status)}>
                {materialRequest.request_status}
              </Tag>
            )}
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
          renderActions(),
        ]}
      >
        {content}
      </Modal>
      <RejectionReasonModal
        open={toolbarRejectOpen}
        documentType={ENTITY_TYPE_MATERIAL_REQUEST}
        documentLabel={materialRequest?.request_number ? `Request ${materialRequest.request_number}` : undefined}
        confirmLoading={rejectMutation.isPending}
        onCancel={() => setToolbarRejectOpen(false)}
        onConfirm={handleToolbarRejectConfirm}
      />
    </>
  );
};
